<i18n>
{
  "en": {
    "createGroupIndexingRuleTitle": "Create demand indexing rule",
    "invalidDemandSelection": "Selected demands can not be the same",
    "emptyFieldError": "Mandatory field",
    "invalidNumberError": "Must be a valid number",
    "isPercentage": "Add a percentage to the reference price",
    "isFixed": "Add a value to the reference price",
    "isActive": "Enable rule for future price recommendation generations",
    "groupOfProductPricing": "Group of products for pricing",
    "groupOfProductPricingHint": "Group for related products for price calculations",
    "groupOfProductPricingError": "Group of product is mandatory",
    "fixedIncrement": "Fixed increment",
    "percentageIncrement": "Percentage increment",
    "selectReferenceGroup": "Select reference demand",
    "selectChangedGroup": "Select changed demand",
    "groupIndexingRuleSubtitle": "Links the prices of the target group to the reference group until the rule is disabled",
    "oneOptionRequired": "Select at least one of the following options"
  },
  "es": {
    "createGroupIndexingRuleTitle": "Crea una regla de indexación de demanda",
    "invalidDemandSelection": "Las demandas elegidas no pueden ser la misma",
    "emptyFieldError": "Campo obligatorio",
    "invalidNumberError": "Tiene que ser un número válido",
    "isPercentage": "Sumar un porcentaje al precio de referencia",
    "isFixed": "Sumar valor al precio de referencia",
    "isActive": "Activar regla para futuras generaciones de recomendaciones de precios",
    "groupOfProductPricing": "Grupo de productos para precios",
    "groupOfProductPricingHint": "Grupo para productos relacionados a la hora de calcular precios",
    "groupOfProductPricingError": "El grupo de productos para precios es obligatorio",
    "fixedIncrement": "Incremento fijo",
    "percentageIncrement": "Incremento de porcentaje",
    "selectReferenceGroup": "Selecciona demanda de referencia",
    "selectChangedGroup": "Selecciona demanda objetivo",
    "groupIndexingRuleSubtitle": "Enlaza los precios de la demanda objetivo a los de la demanda de referencia hasta que se desactive la regla",
    "oneOptionRequired": "Selecciona al menos una de las siguientes opciones"
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if=visible height="100%" class="px-7 py-5">
      <div class="d-flex align-center justify-space-between">
        <v-card-title class="word-break pa-0">
          {{ $t('createGroupIndexingRuleTitle') }}
        </v-card-title>
      </div>
      <v-card-subtitle class="pa-0">{{ $t('groupIndexingRuleSubtitle') }}</v-card-subtitle>
      <v-container class="pa-0">
        <v-form v-model="valid" class="d-flex flex-column align-left" ref="form">
          <v-select v-model="referenceGroup" :items="groupList" :menu-props="{ maxHeight: '400' }"
                    :label="$t('selectReferenceGroup')" item-value="code_name"
                    item-text="translation" class="required" :rules="vSelectRules"/>
          <v-select v-model="changedGroup" :items="groupList" :menu-props="{ maxHeight: '400' }"
                    :label="$t('selectChangedGroup')" item-value="code_name" item-text="translation"
                    class="required" :rules="vSelectRules"/>
          <v-select v-model="groupOfProductPricing" :rules="notNullOrEmptyRule"
                            :items="groupOfProductPricingList"
                            :menu-props="{ maxHeight: '400' }"
                            :label="$t('groupOfProductPricing')" item-value="guid" item-text="name"
                            class="required" :hint="$t('groupOfProductPricingHint')" persistent-hint
                            chips required multiple/>
          <v-divider/>
          <v-card-subtitle class="pa-0 ma-0 mt-2 text-center">
            {{ $t('oneOptionRequired') + ' *' }}
          </v-card-subtitle>
          <v-switch v-model="isFixed" dense :label="$t('isFixed')" @change="onFixed"/>
          <v-text-field v-if="isFixed && !isPercentage" v-model="fixedIncrement"
                        :rules="numberRules"
                        :label="$t('fixedIncrement')"
                        :prefix="currency.hasLeftPosition() ? currency.symbol : ''"
                        :suffix="currency.hasRightPosition() ? currency.symbol : ''"
                        required/>
          <v-switch v-model="isPercentage" dense :label="$t('isPercentage')"
                    @change="onPercentage"/>
          <v-text-field v-if="isPercentage && !isFixed" v-model="percentageIncrement"
                        :rules="numberRules" :label="$t('percentageIncrement')" suffix="%" required/>
          <v-divider/>
          <v-switch v-model="isActive" dense :label="$t('isActive')"/>
          <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm"
                                  :confirmDisabled="!formValid"
                                  :confirmText="$t('confirm')"/>
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import {notEmptyList, notNull, notValidNumberNegative, notNullOrEmpty} from '@core/helpers/formValidators';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapGetters } from '@core/helpers/decorators';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { GroupIndexingRule } from '../models';

@Component
@Components({ DynameatModalButtons })
@MapGetters(['groupList', 'currency', 'groupOfProductPricingList'])
export default class GroupIndexingRuleModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  valid = false;

  percentageIncrement = null;

  fixedIncrement = null;

  changedGroup = null;

  referenceGroup = null;

  isActive = true;

  isFixed = false;

  groupOfProductPricing = null;

  isPercentage = false;

  pointOfSale = null;

  created() {
    this.referenceGroup = undefined;
    this.changedGroup = undefined;
  }

  get vSelectRules() {
    if (this.changedGroup && this.referenceGroup && this.changedGroup === this.referenceGroup) return [this.$t('invalidDemandSelection')];
    return [];
  }

  get numberRules() {
    if (this.isFixed || this.isPercentage) {
      return [notValidNumberNegative(this.$t('invalidNumberError'))];
    }
    return [];
  }

  get formValid() {
    return this.valid && this.formRules;
  }

  get formRules() {
    return ((this.isPercentage && !this.isFixed) || (this.isFixed && !this.isPercentage))
      && (this.changedGroup !== this.referenceGroup);
  }

  get notNullOrEmptyRule() {
    return [notNullOrEmpty(this.$t('groupOfProductPricingError'))];
  }

  onFixed() {
    this.isPercentage = false;
    this.percentageIncrement = null;
  }

  onPercentage() {
    this.isFixed = false;
    this.fixedIncrement = null;
  }

  buildGroupIndexingRule() {
    const isNotPercentageIncrementValid = Number.isNaN(this.percentageIncrement) || this.percentageIncrement === null;
    const percentageIncrement = isNotPercentageIncrementValid ? null : this.percentageIncrement / 100;
    return new GroupIndexingRule(null, percentageIncrement, this.fixedIncrement, this.isActive,
      this.changedGroup, this.referenceGroup, this.groupOfProductPricing, this.$route.params.pointOfSaleGuid);
  }

  onConfirm() {
    if (this.formValid) {
      const groupIndexingRule = this.buildGroupIndexingRule();
      this.clearOwnData();
      this.$emit('confirm', groupIndexingRule);
    }
  }

  onCancel() {
    this.clearOwnData();
    this.$emit('cancel');
  }

  clearOwnData() {
    this.percentageIncrement = null;
    this.fixedIncrement = null;
    this.changedGroup = null;
    this.referenceGroup = null;
    this.isActive = true;
    this.isPercentage = true;
    this.isFixed = false;
    this.pointOfSale = null;
    this.groupOfProductPricing = null;
  }
}
</script>
<style scoped>
.progress-enter-active, .progress-leave-active {
  transition: opacity .5s
}

.progress-enter, .progress-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0
}

.word-break {
  word-break: break-word;
}

.required label::after {
  content: " *";
  color: red;
}
</style>

<style>
.flex-column .v-text-field__details {
  padding-top: 5px;
}
</style>
